import React from "react";
import { CreateVehicleModelRequest } from "@/types";
import {
  getAllLayoutConfigs,
  getDefaultLayoutId,
} from "@/config/vehicleLayouts";
import { useTranslation } from "react-i18next";

interface VehicleModelFormProps {
  vehicleModelData: Partial<CreateVehicleModelRequest>;
  errors: Record<string, string>;
  onUpdateField: (
    field: keyof CreateVehicleModelRequest,
    value: string | number | undefined,
  ) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isEditing: boolean;
}

export const VehicleModelForm: React.FC<VehicleModelFormProps> = ({
  vehicleModelData,
  errors,
  onUpdateField,
  onSubmit,
  onCancel,
  isSubmitting,
  isEditing,
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const currentYear = new Date().getFullYear();
  const availableLayouts = getAllLayoutConfigs();

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:vehicles.form.year")} *
          </label>
          <input
            type="number"
            value={vehicleModelData.year || ""}
            onChange={(e) =>
              onUpdateField("year", parseInt(e.target.value) || undefined)
            }
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.year ? "border-red-500" : "border-secondary-300"
            }`}
            placeholder={t("pages:vehicles.modelForm.yearPlaceholder")}
            min="1900"
            max={currentYear + 5}
            required
          />
          {errors.year && (
            <p className="mt-1 text-sm text-red-600">{errors.year}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:vehicles.form.maker")} *
          </label>
          <input
            type="text"
            value={vehicleModelData.maker || ""}
            onChange={(e) => onUpdateField("maker", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.maker ? "border-red-500" : "border-secondary-300"
            }`}
            placeholder={t("pages:vehicles.modelForm.makerPlaceholder")}
            required
          />
          {errors.maker && (
            <p className="mt-1 text-sm text-red-600">{errors.maker}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          {t("pages:vehicles.form.model")} *
        </label>
        <input
          type="text"
          value={vehicleModelData.model || ""}
          onChange={(e) => onUpdateField("model", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.model ? "border-red-500" : "border-secondary-300"
          }`}
          placeholder={t("pages:vehicles.modelForm.modelPlaceholder")}
          required
        />
        {errors.model && (
          <p className="mt-1 text-sm text-red-600">{errors.model}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          {t("common:description")}
        </label>
        <textarea
          value={vehicleModelData.description || ""}
          onChange={(e) => onUpdateField("description", e.target.value)}
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.description ? "border-red-500" : "border-secondary-300"
          }`}
          placeholder={t("pages:vehicles.modelForm.descriptionPlaceholder")}
          maxLength={1000}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
        <p className="mt-1 text-sm text-secondary-500">
          {t("pages:vehicles.modelForm.characters", {
            count: vehicleModelData.description?.length || 0,
          })}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          {t("pages:vehicles.modelForm.layout")}
        </label>
        <select
          value={vehicleModelData.layoutId || getDefaultLayoutId()}
          onChange={(e) => onUpdateField("layoutId", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.layoutId ? "border-red-500" : "border-secondary-300"
          }`}
        >
          {availableLayouts.map(({ layoutId, config }) => (
            <option key={layoutId} value={layoutId}>
              {config.name}{" "}
              {config.deprecated ? t("pages:vehicles.modelForm.layoutLegacy") : ""}
              {config.description && ` - ${config.description}`}
            </option>
          ))}
        </select>
        {errors.layoutId && (
          <p className="mt-1 text-sm text-red-600">{errors.layoutId}</p>
        )}
        <p className="mt-1 text-sm text-secondary-500">
          {t("pages:vehicles.modelForm.layoutHelp")}
        </p>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50 transition-colors"
          disabled={isSubmitting}
        >
          {t("common:cancel")}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting
            ? t("common:saving")
            : isEditing
              ? t("pages:vehicles.modelForm.update")
              : t("pages:vehicles.modelForm.create")}
        </button>
      </div>
    </form>
  );
};
